import { getProjectFromCollection, projectCollection } from 'collections/project';
import { taskCollection } from 'collections/tasks';
import { planBoardPhaseCollection } from './plan-lane-collection';
const snapshots = new Map();
const toSnapshot = (task)=>{
    var _task_fields, _ref;
    var _find, _task_projectPhase;
    // Convention shared with getTaskCounter (use-project-phase.store.ts): the
    // Status field is the one whose metaFieldValue carries a status `group`.
    const status = (_find = ((_task_fields = task.fields) !== null && _task_fields !== void 0 ? _task_fields : []).find((field)=>{
        var _field_metaFieldValue;
        return (field === null || field === void 0 ? void 0 : (_field_metaFieldValue = field.metaFieldValue) === null || _field_metaFieldValue === void 0 ? void 0 : _field_metaFieldValue.group) != null;
    })) === null || _find === void 0 ? void 0 : _find.metaFieldValue;
    const project = task.project;
    return {
        projectId: typeof project === 'number' ? project : (_ref = project === null || project === void 0 ? void 0 : project.projectId) !== null && _ref !== void 0 ? _ref : undefined,
        phaseId: (_task_projectPhase = task.projectPhase) === null || _task_projectPhase === void 0 ? void 0 : _task_projectPhase.projectPhaseId,
        statusValue: status === null || status === void 0 ? void 0 : status.value,
        group: status === null || status === void 0 ? void 0 : status.group,
        label: status === null || status === void 0 ? void 0 : status.label,
        color: status === null || status === void 0 ? void 0 : status.color
    };
};
const sameCounting = (a, b)=>a.phaseId === b.phaseId && a.statusValue === b.statusValue;
/**
 * Apply a ±1 to the progressStatus entry matching the snapshot's status
 * option. Increments append a fresh entry when the option isn't present yet
 * (first task of that status in the phase); decrements clamp at zero and keep
 * the entry (a zero-count group renders fine).
 */ const applyProgressDelta = (progressStatus, snapshot, delta)=>{
    if (snapshot.statusValue == null || snapshot.group == null) {
        return progressStatus;
    }
    const entries = progressStatus !== null && progressStatus !== void 0 ? progressStatus : [];
    const index = entries.findIndex((entry)=>entry.value === snapshot.statusValue);
    if (index === -1) {
        var _snapshot_label, _snapshot_color;
        if (delta < 0) {
            return progressStatus;
        }
        return [
            ...entries,
            {
                key: String(snapshot.statusValue),
                value: snapshot.statusValue,
                label: (_snapshot_label = snapshot.label) !== null && _snapshot_label !== void 0 ? _snapshot_label : '',
                color: (_snapshot_color = snapshot.color) !== null && _snapshot_color !== void 0 ? _snapshot_color : '',
                group: snapshot.group,
                count: 1
            }
        ];
    }
    return entries.map((entry, i)=>{
        var _entry_count;
        return i === index ? {
            ...entry,
            count: Math.max(0, Number((_entry_count = entry.count) !== null && _entry_count !== void 0 ? _entry_count : 0) + delta)
        } : entry;
    });
};
const patchPhaseAggregates = (snapshot, delta, totalDelta)=>{
    var _snapshot_projectId;
    var _project_projectPhases;
    const { phaseId } = snapshot;
    if (phaseId == null) {
        return;
    }
    // Home 1 — the paginated board record (drives paginatedCounters in lanes).
    const boardEntry = planBoardPhaseCollection.get(phaseId);
    if (boardEntry) {
        planBoardPhaseCollection.push([
            {
                ...boardEntry,
                phase: {
                    ...boardEntry.phase,
                    progressStatus: applyProgressDelta(boardEntry.phase.progressStatus, snapshot, delta),
                    ...boardEntry.phase.totalTaskCount != null && {
                        totalTaskCount: Math.max(0, boardEntry.phase.totalTaskCount + totalDelta)
                    }
                }
            }
        ]);
    }
    // Home 2 — the phase inside the project detail (overview widgets, legacy
    // fallback `boardPhase ?? projectPhase` in the lane header).
    const projectId = (_snapshot_projectId = snapshot.projectId) !== null && _snapshot_projectId !== void 0 ? _snapshot_projectId : boardEntry === null || boardEntry === void 0 ? void 0 : boardEntry.projectId;
    const project = projectId != null ? getProjectFromCollection(projectId) : undefined;
    if (project === null || project === void 0 ? void 0 : (_project_projectPhases = project.projectPhases) === null || _project_projectPhases === void 0 ? void 0 : _project_projectPhases.some((p)=>p.projectPhaseId === phaseId)) {
        projectCollection.push([
            {
                ...project,
                projectPhases: project.projectPhases.map((phase)=>phase.projectPhaseId === phaseId ? {
                        ...phase,
                        progressStatus: applyProgressDelta(phase.progressStatus, snapshot, delta),
                        ...phase.totalTaskCount != null && {
                            totalTaskCount: Math.max(0, phase.totalTaskCount + totalDelta)
                        }
                    } : phase)
            }
        ]);
    }
};
let reconcilerAttached = false;
export const attachPhaseProgressReconciler = ()=>{
    if (reconcilerAttached) {
        return;
    }
    reconcilerAttached = true;
    taskCollection.subscribeToChanges((changedIds)=>{
        changedIds.forEach((id)=>{
            if (typeof id !== 'number') {
                return;
            }
            const task = taskCollection.get(id);
            const previous = snapshots.get(id);
            if (task === undefined) {
                // Deleted: remove its contribution from the phase it counted under.
                if (previous) {
                    patchPhaseAggregates(previous, -1, -1);
                    snapshots.delete(id);
                }
                return;
            }
            const next = toSnapshot(task);
            if (!previous) {
                // First sight = baseline. The server aggregates already (or will,
                // via the next board fetch) include this task — counting it here
                // would double it.
                snapshots.set(id, next);
                return;
            }
            if (!sameCounting(previous, next)) {
                const phaseChanged = previous.phaseId !== next.phaseId;
                // Same phase: totals unchanged, only the group shifts.
                // Cross-phase: the task's whole contribution moves.
                patchPhaseAggregates(previous, -1, phaseChanged ? -1 : 0);
                patchPhaseAggregates(next, 1, phaseChanged ? 1 : 0);
            }
            snapshots.set(id, next);
        });
    });
};
/** Test hook: forget every baseline (module-level singletons leak state). */ export const resetPhaseProgressSnapshots = ()=>{
    snapshots.clear();
};
